import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppThunkAPIType } from "../../../app/appTypes"
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../http"
import { DateFromModeEnum, DateToModeEnum, IntervalUnitEnum } from "../../common/commonTypes"

export interface CompanyDocumentType {
    id: number
    companyId: number
    code: string
    title: string
    description: string
    isActive: boolean
    displayOrder: number
    color: string
    isQmsEnabled: boolean
    qmsSettings: CompanyDocumentTypeQmsSettings
}

export interface CompanyDocumentTypeQmsSettings {
    id?: number
    companyDocumentTypeId?: number
    requiresEffectiveFrom: boolean
    requiresEffectiveTo: boolean
    effectiveFromMode: DateFromModeEnum
    effectiveToMode: DateToModeEnum
    validityIntervalValue: number
    validityIntervalUnit: IntervalUnitEnum
    requiresReview: boolean
    reviewIntervalValue: number
    reviewIntervalUnit: IntervalUnitEnum
    requiresControlledPrints: boolean
}

export type SaveCompanyDocumentType = Omit<CompanyDocumentType, "id" | "companyId" | "qmsSettings">

export const loadCompanyDocumentTypes = createAsyncThunk<
    CompanyDocumentType[],
    { includeInactive?: boolean } | void,
    AppThunkAPIType
>(
    "companyDocumentTypes/loadAll",
    async (params, { dispatch, rejectWithValue }) => {
        const includeInactive = params?.includeInactive ?? false
        const result = await dispatch(getWithAuth({ url: `api/company-document-types?includeInactive=${includeInactive}` }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as CompanyDocumentType[]
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" })
        }
    }
)

export const createCompanyDocumentType = createAsyncThunk<
    CompanyDocumentType,
    SaveCompanyDocumentType,
    AppThunkAPIType
>(
    "companyDocumentTypes/create",
    async (dto, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/company-document-types`,
            payload: dto
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as CompanyDocumentType
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" })
        }
    }
)

export const updateCompanyDocumentType = createAsyncThunk<
    CompanyDocumentType,
    { id: number; dto: SaveCompanyDocumentType },
    AppThunkAPIType
>(
    "companyDocumentTypes/update",
    async ({ id, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/company-document-types/${id}`,
            payload: dto
        }))
        const { payload } = result

        if (putWithAuth.fulfilled.match(result)) {
            return payload as CompanyDocumentType
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" })
        }
    }
)

export const deleteCompanyDocumentType = createAsyncThunk<
    number,
    number,
    AppThunkAPIType
>(
    "companyDocumentTypes/delete",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(deleteWithAuth({
            url: `api/company-document-types/${id}`
        }))
        const { payload } = result

        if (deleteWithAuth.fulfilled.match(result)) {
            return id
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" })
        }
    }
)


export const updateQmsSettings = createAsyncThunk<
    CompanyDocumentTypeQmsSettings,
    { id: number; dto: CompanyDocumentTypeQmsSettings },
    AppThunkAPIType
>(
    "companyDocumentTypes/updateQmsSettings",
    async ({ id, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/company-document-types/${id}/qms-settings`,
            payload: dto
        }))
        const { payload } = result

        if (putWithAuth.fulfilled.match(result)) {
            return payload as CompanyDocumentTypeQmsSettings
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" })
        }
    }
)
